import React, { createContext, Dispatch, SetStateAction, useContext, useEffect, useState } from "react";
import { useThaloWeb } from "@/contexts/ThaloWebContext";
// import { useSearchParams } from "react-router-dom";
import { useAllAssets } from "@/contexts/AllAssetsContext";
import { InstallationData } from "@/installation/installation";

type SelectedAssetsContextType = {
  selectedAssetIds: number[]
  setSelectedAssetIds: Dispatch<SetStateAction<number[]>>
  selectedAssets: InstallationData[]
  multipleAssetsSelected: boolean
};

const SelectedAssetsContext = createContext<SelectedAssetsContextType | undefined>(undefined);

export function SelectedAssetsContextProvider({ children }: any) {
  const { allAssets } = useAllAssets();

  // const [searchParams, setSearchParams] = useSearchParams();

  const searchParams = { get: (foo: any) => {}, getAll: (foo: any) => [] };
  const setSearchParams = (foo: any) => {};

  const paramKey = "assetId"

  const initialState: number[] = [];

  const [selectedAssetIds, setSelectedAssetIds] = useState<number[]>(() => {
    // if (typeof window === "undefined") return initialState;

    const paramValue = searchParams.getAll(paramKey);

    return paramValue !== null ? paramValue.map(id => parseInt(id)) : initialState;
  });

  const selectedAssets = allAssets.filter((asset) => selectedAssetIds.includes(asset.id))

  useEffect(() => {
    const searchParamsAssetIds = searchParams.getAll(paramKey);
    if (JSON.stringify(searchParamsAssetIds) !== JSON.stringify(selectedAssetIds)) {
      setSearchParams(
        (prev) => {
          prev.delete(paramKey)
          selectedAssetIds.forEach((assetId) => {
            prev.append(paramKey, assetId.toString());
          })
          return prev;
        },
        { replace: true }
      );
    }
  }, [selectedAssetIds, searchParams]);

  return (
    <SelectedAssetsContext.Provider
      value={{
        selectedAssetIds,
        setSelectedAssetIds,
        selectedAssets,
        multipleAssetsSelected: selectedAssetIds.length > 1
      }}
    >
      {children}
    </SelectedAssetsContext.Provider>
  );
}

type useSelectedAssetsOptions = {
  selectFirstAssetByDefault: boolean;
  enforceSingleSelection?: boolean;
}

const defaultUseSelectedAssetsOptions: useSelectedAssetsOptions = {
  selectFirstAssetByDefault: false,
  enforceSingleSelection: false
}

export function useSelectedAssets(options: useSelectedAssetsOptions = defaultUseSelectedAssetsOptions) {
  const context = useContext(SelectedAssetsContext);
  if (context === undefined) {
    throw new Error("Context must be used within a Provider");
  }

  const { selectedLocation } = useThaloWeb();
  const { allAssets } = useAllAssets();
  const { selectFirstAssetByDefault, enforceSingleSelection } = options;

  useEffect(() => {
    if (selectFirstAssetByDefault && selectedLocation && allAssets.length > 0) {
      const firstAssetWithSensorKit = allAssets
        .sort((asset1, asset2) => asset1.sensor_kit ? -1 : 1)
        .at(0)
      const assetIdsToSelect = [firstAssetWithSensorKit!.id];
      if (JSON.stringify(assetIdsToSelect) !== JSON.stringify(context.selectedAssetIds)) {
        context.setSelectedAssetIds(assetIdsToSelect)
      }
    }
  }, [selectedLocation]);

  // Enforce single selection if enabled
  useEffect(() => {
    if (enforceSingleSelection && context.selectedAssetIds.length > 1) {
      context.setSelectedAssetIds([context.selectedAssetIds[0]]);
    }
  }, [context.selectedAssetIds, enforceSingleSelection]);

  return context;
}
